import { DateTime } from 'luxon'

import { User as DashboardUser } from './models'

// github.com/fingerprintjs/legendary-blade/blob/4b261b959b64999487d37b3bfa3a3e46af01ccc4/context/AuditLogging/Application/ListPublicAuditLogs.ts#L4
export interface ListPublicAuditLogsParams {
  page: number
  perPage: number
  since?: string
  until?: string
  actionCode?: string
  message?: string
  status?: 'success' | 'error'
  targetType?: AuditableResource[]
  actorId?: string[]
  targetId?: string
  workspaceId?: string
  accountId?: string
  env?: string
  serviceName?: string
}

// https://github.com/fingerprintjs/legendary-blade/blob/b98dc2597702e307b43f30247035bb18028dadd0/context/AuditLogging/Domain/ValueObjects/TargetType.ts#L4
export enum AuditableResource {
  User = 'User',
  Account = 'Account',
  Workspace = 'Workspace',
  WorkspaceEnvironment = 'WorkspaceEnvironment',
  SubscriptionToken = 'SubscriptionToken',
  SslCertificate = 'SslCertificate',
  TrafficRule = 'TrafficRule',
  TrafficRulesV2 = 'TrafficRulesV2',
  Ruleset = 'Ruleset',
  AlertSetting = 'AlertSetting',
  SubscriptionWebhook = 'SubscriptionWebhook',
  WebhookRule = 'WebhookRule',
  CloudflareIntegration = 'CloudflareIntegration',
  CloudfrontIntegration = 'CloudFrontIntegration',
  Invoice = 'Invoice',
  PaymentMethod = 'PaymentMethod',
  PaymentIntent = 'PaymentIntent',
  UsageCounter = 'UsageCounter',
  ApplicationEncryptionKey = 'ApplicationEncryptionKey',
  ApplicationProduct = 'ApplicationProduct',
  FlowDeployment = 'FlowDeployment',
  ProtectedDomain = 'ProtectedDomain',
  UserInvitation = 'UserInvitation',
  Coupon = 'Coupon',
  Discount = 'Discount',
  Promotion = 'Promotion',
  PlanConfiguration = 'PlanConfiguration',
  SubscriptionPlan = 'SubscriptionPlan',
  SubscriptionItem = 'SubscriptionItem',
  SubscriptionBillingPlan = 'SubscriptionBillingPlan',
  System = 'System',
  FraudLabelImport = 'FraudLabelImport',
  FraudScore = 'FraudScore',
}

export enum CommonAuditAction {
  Created = 'created',
  Updated = 'updated',
  Deleted = 'deleted',
  Renamed = 'nameUpdated',
}

export enum WorkspaceAuditAction {
  Reactivated = 'reactivate',
}

export enum ApplicationEncryptionKeyAuditAction {
  Rotated = 'rotated',
}

export enum TrafficRuleV2AuditAction {
  BulkUpdated = 'rulesBulkUpdated',
}

export enum FraudLabelImportAuditAction {
  Accepted = 'accepted',
  Rejected = 'rejected',
  Updated = 'updated',
  Processed = 'processed',
}

export enum FraudScoreAuditAction {
  Trained = 'trained',
}

export enum SslCertificateAuditAction {
  Migrated = 'migrated',
  MigrationCompleted = 'migrationCompleted',
  StatusChanged = 'status_changed',
}

export type AuditAction =
  | CommonAuditAction
  | WorkspaceAuditAction
  | ApplicationEncryptionKeyAuditAction
  | TrafficRuleV2AuditAction
  | FraudLabelImportAuditAction
  | FraudScoreAuditAction
  | SslCertificateAuditAction

export type AuditLogUser = Pick<DashboardUser, 'id' | 'status' | 'name' | 'email' | 'role' | 'timezone'>

export interface AuditLog {
  id: string
  timestamp: DateTime
  actionCode: `${AuditableResource}.${AuditAction}`
  message: string
  status: 'success' | 'error'
  payload: Record<string, unknown>
  actorType: 'user' | 'system'
  actorId: string
  targetType: AuditableResource
  targetId: string
  workspaceId: string
  accountId: string
  serviceName: string
  initialState: Record<string, unknown>
  endState: Record<string, unknown>
}

export type SerializedAuditLog = Omit<AuditLog, 'timestamp'> & { timestamp: string }
